import { useEffect, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  fetchNutrientSourcesRange,
  useNutrientsRange,
  type NutrientKey,
  type PeriodNutrient,
  type PeriodSource,
} from '@/hooks/useMicronutrients';
import { fmtNum } from '@/utils/format';

/**
 * Микронутриенты за период: среднее против нормы, сумма, график по дням
 * и продукты, из которых это набралось (06.10.2026).
 *
 * Ни одного запроса к модели: всё складывается из того, что посчитано при записи еды.
 */

const STATUS_HEX = { good: '#10E58E', warning: '#FFB454', lack: '#FF6B6B' } as const;
const STATUS_TEXT = { good: 'text-emerald-glow', warning: 'text-warn', lack: 'text-danger' } as const;
const STATUS_BG = { good: 'bg-emerald-glow', warning: 'bg-warn', lack: 'bg-danger' } as const;

const GROUPS: { label: string; emoji: string; keys: NutrientKey[] }[] = [
  { label: 'Пищевой профиль', emoji: '🍽', keys: ['fiber', 'vegetables_fruits'] },
  { label: 'Минералы', emoji: '💎', keys: ['calcium', 'iron', 'magnesium', 'zinc', 'potassium'] },
  { label: 'Витамины', emoji: '☀️', keys: ['vitamin_d', 'vitamin_b12', 'vitamin_c'] },
  { label: 'Жиры', emoji: '🐟', keys: ['omega3', 'saturated_fat'] },
];

function dayWord(n: number): string {
  const ten = n % 100;
  const one = n % 10;
  if (ten >= 11 && ten <= 14) return 'дней';
  if (one === 1) return 'день';
  if (one >= 2 && one <= 4) return 'дня';
  return 'дней';
}

export function NutrientsPeriod({
  from,
  to,
  onPickDay,
}: {
  from: string;
  to: string;
  /** Тап по столбику графика — открыть этот день подробно. */
  onPickDay: (date: string) => void;
}) {
  const { nutrients, loading, error, daysWithData, stats } = useNutrientsRange(from, to);
  const [open, setOpen] = useState<NutrientKey | null>(null);
  const totalDays = differenceInCalendarDays(parseISO(to), parseISO(from)) + 1;

  // Сменился период — раскрытая карточка относится уже к другим данным
  useEffect(() => { setOpen(null); }, [from, to]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 size={28} className="animate-spin text-emerald-glow mb-3" />
        <div className="text-sm text-ink-dim">Складываю записи за период…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-elevated p-5 text-center mt-4">
        <div className="text-sm text-ink mb-1">Не получилось загрузить период</div>
        <div className="text-xs text-ink-dim">Проверь интернет и попробуй ещё раз.</div>
      </div>
    );
  }

  if (!daysWithData) {
    return (
      <div className="card-elevated p-6 text-center mt-4">
        <div className="text-4xl mb-3">🗓</div>
        <div className="font-display font-bold text-base mb-1.5">За этот период записей нет</div>
        <p className="text-sm text-ink-dim leading-relaxed">
          Полистай назад или выбери другие даты — микронутриенты считаются по дням, когда ты записывал еду.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Сколько дней на самом деле стоит за цифрами — без этого среднее читается неверно */}
      <div className="card p-3 mb-3">
        <div className="text-sm text-ink">
          Записи есть за <span className="font-display font-bold text-emerald-glow">{daysWithData}</span> из {totalDays} {dayWord(totalDays)}
        </div>
        <div className="text-[11px] text-ink-dim mt-0.5 leading-snug">
          Среднее считаю только по дням с записями: пропущенный день — это «не записал», а не «съел ноль».
        </div>
      </div>

      <div className="card-elevated p-3 grid grid-cols-3 gap-2 mb-5">
        <Stat label="В норме" value={stats.good} total={stats.total} color="text-emerald-glow" />
        <Stat label="Близко" value={stats.warning} total={stats.total} color="text-warn" />
        <Stat label="Не хватает" value={stats.lack} total={stats.total} color="text-danger" />
      </div>

      <div className="space-y-5">
        {GROUPS.map((g) => {
          const items = g.keys.map((k) => nutrients.find((n) => n.key === k)).filter(Boolean) as PeriodNutrient[];
          if (!items.length) return null;
          return (
            <div key={g.label}>
              <div className="flex items-baseline gap-2 mb-2 px-1">
                <span className="text-sm">{g.emoji}</span>
                <h2 className="text-xs uppercase tracking-wider text-ink-dim font-medium">{g.label}</h2>
              </div>
              <div className="space-y-2">
                {items.map((n) => (
                  <PeriodCard
                    key={n.key}
                    n={n}
                    from={from}
                    to={to}
                    days={daysWithData}
                    open={open === n.key}
                    onToggle={() => setOpen(open === n.key ? null : n.key)}
                    onPickDay={onPickDay}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  return (
    <div className="text-center">
      <div className={`font-display text-xl font-bold ${color}`}>
        {value}
        <span className="text-[10px] text-ink-dim font-body ml-0.5">/{total}</span>
      </div>
      <div className="text-[10px] text-ink-dim uppercase tracking-wider">{label}</div>
    </div>
  );
}

function PeriodCard({
  n, from, to, days, open, onToggle, onPickDay,
}: {
  n: PeriodNutrient;
  from: string;
  to: string;
  days: number;
  open: boolean;
  onToggle: () => void;
  onPickDay: (date: string) => void;
}) {
  const isReverse = n.target_type === 'reverse';
  const isRange = n.target_type === 'range' && !!n.target_max;
  const targetText = isRange
    ? `${fmtNum(n.target)}–${fmtNum(n.target_max!)} ${n.unit}`
    : isReverse ? `до ${fmtNum(n.target)} ${n.unit}` : `${fmtNum(n.target)} ${n.unit}`;

  return (
    <div className="card p-3">
      <button type="button" onClick={onToggle} className="w-full text-left">
        <div className="flex items-baseline justify-between mb-1.5 gap-2">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-base shrink-0">{n.emoji}</span>
            <span className="font-medium text-sm text-ink truncate">{n.label}</span>
          </div>
          <div className="text-xs font-display font-semibold shrink-0">
            <span className={STATUS_TEXT[n.status]}>{fmtNum(n.avg)}</span>
            <span className="text-ink-dim"> / {targetText}</span>
          </div>
        </div>

        <div className="relative h-1.5 bg-bg-elevated rounded-full overflow-hidden mb-1.5">
          <div className={`h-full rounded-full ${STATUS_BG[n.status]}`} style={{ width: `${Math.min(100, n.percentage)}%` }} />
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] text-ink-dim">
            в среднем за день · {isReverse ? 'в пределах лимита' : 'в норме'}{' '}
            <span className={STATUS_TEXT[n.daysGood >= days / 2 ? 'good' : n.daysGood > 0 ? 'warning' : 'lack']}>
              {n.daysGood} из {days}
            </span>{' '}
            {dayWord(days)}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-ink-dim shrink-0">
            всего {fmtNum(n.total)} {n.unit}
            <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {open && <PeriodDetail n={n} from={from} to={to} onPickDay={onPickDay} />}
    </div>
  );
}

function PeriodDetail({
  n, from, to, onPickDay,
}: {
  n: PeriodNutrient;
  from: string;
  to: string;
  onPickDay: (date: string) => void;
}) {
  const [sources, setSources] = useState<PeriodSource[] | null>(null);
  const isReverse = n.target_type === 'reverse';

  useEffect(() => {
    let cancelled = false;
    setSources(null);
    fetchNutrientSourcesRange(from, to, n.key).then((s) => { if (!cancelled) setSources(s); });
    return () => { cancelled = true; };
  }, [from, to, n.key]);

  const data = n.series.map((p) => ({
    date: p.date,
    label: format(parseISO(p.date), 'd.MM'),
    value: p.value,
    status: p.status,
  }));
  // Верх шкалы — чтобы линия нормы всегда была видна, даже если её ни разу не достигли
  const top = Math.max(n.target * 1.15, ...data.map((d) => d.value)) || 1;

  return (
    <div className="mt-3 pt-3 border-t border-line/60">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[11px] text-ink-dim">По дням · пунктир — {isReverse ? 'лимит' : 'норма'}</div>
        <div className="text-[10px] text-ink-dim">нажми на столбик — откроется день</div>
      </div>
      {/* Зелёный столбик может стоять ниже пунктира: «в норме» считается от 80% —
          так же, как на дневном экране. Без подписи это выглядит ошибкой. */}
      {!isReverse && n.target_type !== 'range' && (
        <div className="text-[10px] text-ink-dim mb-1">зелёный — от 80% нормы, жёлтый — от 50%</div>
      )}
      <div className="h-36 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: -22 }}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#5A6573' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={14} />
            <YAxis domain={[0, top]} tick={{ fontSize: 9, fill: '#5A6573' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => fmtNum(Math.round(v))} width={44} />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              contentStyle={{ background: '#141A1F', border: '1px solid #222B33', borderRadius: 10, fontSize: 11 }}
              labelStyle={{ color: '#8B98A5' }}
              formatter={(v: number) => [`${fmtNum(v)} ${n.unit}`, n.label]}
              labelFormatter={(_l: string, payload: any[]) => {
                const d = payload?.[0]?.payload?.date;
                return d ? format(parseISO(d), 'd MMMM, EEEEEE', { locale: ru }) : '';
              }}
            />
            <ReferenceLine y={n.target} stroke="#8B98A5" strokeDasharray="4 3" strokeWidth={1} />
            <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={22} onClick={(d: any) => d?.date && onPickDay(d.date)}>
              {data.map((d) => (
                <Cell key={d.date} fill={STATUS_HEX[d.status]} cursor="pointer" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3">
        <div className="text-[11px] text-ink-dim mb-1.5">{isReverse ? 'Откуда набралось' : 'Из чего набралось'} за период</div>
        {sources === null ? (
          <div className="text-[11px] text-ink-dim flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" /> считаю…
          </div>
        ) : sources.length === 0 ? (
          <div className="text-[11px] text-ink-dim">В записях за период этого нутриента почти нет.</div>
        ) : (
          <div className="space-y-1">
            {sources.slice(0, 6).map((s) => (
              <div key={s.name} className="flex items-baseline gap-2 text-[11px]">
                <span className="flex-1 min-w-0 truncate text-ink-muted">{s.name}</span>
                <span className="text-ink-dim shrink-0">{s.times > 1 ? `${s.times} раз · ` : ''}{fmtNum(s.amount)} {n.unit}</span>
                <span className="text-ink w-9 text-right shrink-0">{s.share}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
